import * as React from 'react';
import { ApiClientError } from '@/api/http';

/**
 * Data fetching.
 *
 * A deliberately small hook rather than a query library: every list and detail page
 * needs the same four things — a loading flag, the data, a user-facing error, and a
 * `refetch` to call after a mutation — and nothing here needs a cache that outlives
 * the component. Requests are aborted on unmount and on dependency change, so a slow
 * response for a page the user already left never lands in state.
 *
 * A 401 is handled once, centrally: in oidc mode the session ended mid-use, and the
 * cleanest recovery is to restart the shell (which finds no session and redirects to
 * sign-in) rather than leave the user clicking controls that silently fail. The
 * handler is installed by the app shell via {@link setUnauthorizedHandler}.
 */

let onUnauthorized: (() => void) | undefined;

/** Installed once by the shell. A 401 from any `useApiData` call invokes it. */
export function setUnauthorizedHandler(handler: (() => void) | undefined): void {
  onUnauthorized = handler;
}

export interface AsyncData<T> {
  data: T | undefined;
  loading: boolean;
  /** A message already written for a user, or undefined. */
  error: string | undefined;
  refetch: () => void;
}

export function useApiData<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: React.DependencyList,
): AsyncData<T> {
  const [data, setData] = React.useState<T | undefined>(undefined);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [nonce, setNonce] = React.useState(0);

  // The fetcher closes over the deps the caller passes; those are the real
  // dependencies, so we intentionally key the effect on them plus the refetch nonce.
  const fetcherRef = React.useRef(fetcher);
  fetcherRef.current = fetcher;

  React.useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(undefined);

    fetcherRef.current(controller.signal).then(
      (result) => {
        if (controller.signal.aborted) return;
        setData(result);
        setLoading(false);
      },
      (cause) => {
        if (controller.signal.aborted) return;
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        if (cause instanceof ApiClientError && cause.status === 401) {
          onUnauthorized?.();
          return;
        }
        setError(messageFor(cause));
        setLoading(false);
      },
    );

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const refetch = React.useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, error, refetch };
}

/** Turns any thrown value into a message safe to show a user. */
export function messageFor(cause: unknown): string {
  if (cause instanceof ApiClientError) return cause.message;
  if (cause instanceof Error && cause.message) return cause.message;
  return 'Something went wrong. Please try again.';
}
